import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { Link } from 'react-router-dom';
import { logger } from '../../utils/logger';

function ResetPassword() {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [status, setStatus] = useState('');
    const [formSubmitting, setFormSubmitting] = useState(false);

    const { token } = useParams();
    logger.log(token);

    // Animation hook for form entry
    const [formRef, isFormVisible] = useScrollAnimation({ threshold: 0.3 });

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setStatus('Passwords do not match');
            return;
        }
        
        setFormSubmitting(true);
        logger.log(password);

        try {
            const response = await axios.post(API_ENDPOINTS.resetPassword, {
                old_password: password,
                new_password: confirmPassword,
                token: token
            });
            logger.log(response);
            setStatus('Password reset successful');
        } catch (error) {
            logger.error(error);
            setStatus('Password reset failed');
        } finally {
            setFormSubmitting(false);
        }
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col relative">
            {/* Logo in top left corner */}
            <div className={`absolute top-6 left-6 z-10 transition-all duration-1000 ease-out transform ${
                isFormVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}>
                <Link to="/">
                    <img src="/img/logo.png" alt="AmpTrans Logo" className="w-auto h-12" />
                </Link>
            </div>
            
            {/* Main content container - centered */}
            <div className="flex-1 flex items-center justify-center px-4 py-20">
                <div
                    ref={formRef}
                    className={`w-full max-w-2xl transition-all duration-1000 ease-out transform ${
                        isFormVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                    }`}
                >
                    <div className="w-full flex justify-center items-center p-8">
                        <div className="w-full bg-white rounded-lg p-8 shadow-lg">
                            {/* Header - Centered */}
                            <div className="text-center mb-8">
                                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-2 font-ui">Reset Your Password</h2>
                                <p className="text-sm sm:text-base text-gray-600 font-ui">Enter your new password below</p>
                            </div>
                            
                            <form onSubmit={handleResetPassword} className="space-y-6">
                                {/* New Password Input */}
                                <div className="text-center">
                                    <label htmlFor="password" className="block mb-2 text-gray-800 font-ui font-medium text-sm text-left">
                                        New Password
                                    </label>
                                    <div className="relative">
                                        <input 
                                            type={showPassword ? 'text' : 'password'} 
                                            className="w-full px-4 py-3 pr-12 rounded-lg bg-gray-100 border-none font-ui focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 text-left" 
                                            id="password" 
                                            onChange={(e) => setPassword(e.target.value)} 
                                            required
                                            disabled={formSubmitting}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword((visible) => !visible)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 transition-colors duration-200 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                                            disabled={formSubmitting}
                                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                                        >
                                            {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                                        </button>
                                    </div>
                                </div>
                                
                                {/* Confirm Password Input */}
                                <div className="text-center">
                                    <label htmlFor="confirm-password" className="block mb-2 text-gray-800 font-ui font-medium text-sm text-left">
                                        Confirm Password
                                    </label>
                                    <div className="relative">
                                        <input 
                                            type={showConfirmPassword ? 'text' : 'password'} 
                                            className="w-full px-4 py-3 pr-12 rounded-lg bg-gray-100 border-none font-ui focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 text-left" 
                                            id="confirm-password" 
                                            onChange={(e) => setConfirmPassword(e.target.value)} 
                                            required
                                            disabled={formSubmitting}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword((visible) => !visible)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 transition-colors duration-200 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                                            disabled={formSubmitting}
                                            aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                                        >
                                            {showConfirmPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                                        </button>
                                    </div>
                                </div>
                                
                                {/* Submit Button - Centered */}
                                <div className="flex justify-center">
                                    <button 
                                        type="submit" 
                                        className="w-full font-ui uppercase text-white bg-gradient-to-r from-primary to-primary-light py-3 px-8 tracking-wider text-sm font-medium rounded-lg border-0 transition-all duration-500 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                        disabled={formSubmitting}
                                    >
                                        {formSubmitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                Resetting...
                                            </>
                                        ) : (
                                            'Reset Password'
                                        )}
                                    </button>
                                </div>
                            </form>
                            
                            {/* Status Message - Centered */}
                            {status && (
                                <div className="text-center mt-4">
                                    <p className={`text-sm font-ui ${
                                        status === 'Password reset successful' 
                                            ? 'text-green-600' 
                                            : 'text-red-600'
                                    }`}>
                                        {status}
                                    </p>
                                </div>
                            )}
                            
                            {/* Back to Login - Centered */}
                            <div className="text-center mt-6 pt-4 border-t border-gray-200">
                                <Link 
                                    to="/login" 
                                    className="font-ui bg-transparent border-none text-blue-500 underline cursor-pointer text-sm hover:text-blue-600 transition-colors duration-200"
                                >
                                    Back to login
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ResetPassword;